import React, { useState } from 'react';
import {
  Clipboard,
  Plus,
  Search,
  Edit2,
  Trash2,
  Download,
  Filter,
  Laptop,
  Smartphone,
  Car,
  Monitor,
  Package,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { usePermissions } from '../../rbac/PermissionsContext';

const Assets = () => {
  const { hasModuleAccess, hasScreenAccess, isAdmin } = usePermissions();
  const canViewAssetList =
    isAdmin || hasScreenAccess('assets', 'asset_list', 'view');
  const canExportAssets =
    isAdmin ||
    hasModuleAccess('assets', 'export') ||
    hasScreenAccess('assets', 'asset_reports', 'export') ||
    hasScreenAccess('assets', 'asset_list', 'export');
  const canCreateAssets =
    isAdmin ||
    hasModuleAccess('assets', 'create') ||
    hasScreenAccess('assets', 'asset_list', 'create');
  const canEditAssets =
    isAdmin ||
    hasModuleAccess('assets', 'edit') ||
    hasScreenAccess('assets', 'asset_list', 'edit');
  const canDeleteAssets =
    isAdmin ||
    hasModuleAccess('assets', 'delete') ||
    hasScreenAccess('assets', 'asset_list', 'delete');
  const canViewAssets =
    isAdmin ||
    hasModuleAccess('assets', 'view') ||
    canViewAssetList ||
    hasScreenAccess('assets', 'assign_asset', 'view') ||
    hasScreenAccess('assets', 'asset_reports', 'view');

  const [showForm, setShowForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const assetTypes = [
    { id: 'computer', label: 'Computer', icon: <Laptop className="w-4 h-4" /> },
    { id: 'phone', label: 'Phone', icon: <Smartphone className="w-4 h-4" /> },
    { id: 'vehicle', label: 'Vehicle', icon: <Car className="w-4 h-4" /> },
    { id: 'monitor', label: 'Monitor', icon: <Monitor className="w-4 h-4" /> },
    { id: 'other', label: 'Other', icon: <Package className="w-4 h-4" /> },
  ];

  const assets = [
    {
      id: 1,
      name: 'MacBook Pro M1',
      type: 'computer',
      serialNumber: 'MBA2023001',
      assignedTo: 'John Smith',
      department: 'Engineering',
      status: 'active',
      purchaseDate: '2023-01-15',
      warranty: '2024-01-15',
      value: 1299.99,
      condition: 'excellent',
    },
    // Add more sample data as needed
  ];

  const handleOpenAddAsset = () => {
    if (!canCreateAssets) {
      toast.error('You do not have permission to add assets.');
      return;
    }
    setShowForm(true);
  };

  const handleExportReport = () => {
    if (!canExportAssets) {
      toast.error('You do not have permission to export asset reports.');
      return;
    }
    toast('Asset export flow is not wired yet in this module.', { duration: 3200 });
  };

  const handleEditAsset = () => {
    if (!canEditAssets) {
      toast.error('You do not have permission to edit assets.');
      return;
    }
  };

  const handleDeleteAsset = () => {
    if (!canDeleteAssets) {
      toast.error('You do not have permission to delete assets.');
      return;
    }
  };

  const handleSaveAsset = (e) => {
    e.preventDefault();
    if (!canCreateAssets && !canEditAssets) {
      toast.error('You do not have permission to save asset records.');
      return;
    }
    setShowForm(false);
  };

  if (!canViewAssets) {
    return (
      <div className="flex min-h-[calc(100dvh-5rem)] items-center justify-center bg-white p-6">
        <div className="max-w-md rounded-xl border border-gray-200 bg-white p-6 text-center text-gray-700">
          <h4 className="mb-1 text-sm font-semibold uppercase tracking-wide">No assets access</h4>
          <p className="text-xs">
            You do not currently have permission to view Assets sections.
            Please contact your administrator.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 font-sans antialiased">
      {/* Header Section */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center space-x-2">
          <Clipboard className="w-6 h-6 text-redhalo-navy dark:text-redhalo-mint" />
          <h1 className="text-xl font-bold uppercase tracking-wide text-gray-900 dark:text-gray-100">
            Asset Management
          </h1>
        </div>
        <button
          type="button"
          onClick={handleOpenAddAsset}
          disabled={!canCreateAssets}
          className="flex items-center px-4 py-2 rh-btn-primary rounded-lg hover:opacity-95 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/50 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-[#121212]"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add New Asset
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Search assets..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-[#3C3C4C] rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-gray-400" />
          <select
            className="w-full py-2 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="assigned">Assigned</option>
            <option value="maintenance">In Maintenance</option>
            <option value="retired">Retired</option>
          </select>
        </div>
        <button
          type="button"
          onClick={handleExportReport}
          disabled={!canExportAssets}
          className="flex items-center justify-center px-4 py-2 bg-gray-100 dark:bg-[#2a2a2a] text-gray-700 dark:text-gray-200 border border-transparent dark:border-[#3C3C4C] rounded-lg hover:bg-gray-200 dark:hover:bg-[#333] transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40"
        >
          <Download className="w-4 h-4 mr-2" />
          Export Report
        </button>
      </div>

      {/* Assets Table */}
      <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#3C3C4C] rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-200/80 dark:bg-[#2D2D2D]">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Asset Details
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Assignment
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Purchase Info
                </th>
                <th className="px-6 py-3 text-left text-xs font-bold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#3C3C4C]">
              {assets.map((asset) => (
                <tr key={asset.id} className="hover:bg-gray-50/80 dark:hover:bg-[#2a2a2a]/80">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      {assetTypes.find(type => type.id === asset.type)?.icon}
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{asset.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400">SN: {asset.serialNumber}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{asset.assignedTo}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">{asset.department}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full
                      ${asset.status === 'active' ? 'bg-green-100 text-green-800' : 
                        asset.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' : 
                        'bg-gray-100 text-gray-800'}`}>
                      {asset.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100">${asset.value}</div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">Purchased: {asset.purchaseDate}</div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium">
                    <div className="flex space-x-2">
                      <button
                        type="button"
                        onClick={handleEditAsset}
                        disabled={!canEditAssets}
                        className={`text-redhalo-navy dark:text-redhalo-mint hover:text-redhalo-red focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 rounded ${
                          !canEditAssets ? 'cursor-not-allowed opacity-45' : ''
                        }`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={handleDeleteAsset}
                        disabled={!canDeleteAssets}
                        className={`text-redhalo-red hover:text-redhalo-red-dark dark:text-rose-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-redhalo-red/40 rounded ${
                          !canDeleteAssets ? 'cursor-not-allowed opacity-45' : ''
                        }`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Asset Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Add New Asset</h2>
              <button
                onClick={() => setShowForm(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ×
              </button>
            </div>
            <form className="grid grid-cols-2 gap-4" onSubmit={handleSaveAsset}>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Asset Name
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[#3C3C4C] rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent"
                  placeholder="Enter asset name"
                />
              </div>
              
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Asset Type
                </label>
                <select className="w-full px-3 py-2 border border-gray-300 dark:border-[#3C3C4C] rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent">
                  {assetTypes.map(type => (
                    <option key={type.id} value={type.id}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1">
                  Serial Number
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-[#3C3C4C] rounded-lg bg-white dark:bg-[#2a2a2a] text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-redhalo-red/35 focus:border-transparent"
                  placeholder="Enter serial number"
                />
              </div>

              {/* Add more form fields as needed */}

              <div className="col-span-2 flex justify-end space-x-2 mt-4">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!canCreateAssets && !canEditAssets}
                  className="px-4 py-2 rh-btn-primary rounded-lg hover:opacity-95"
                >
                  Save Asset
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-6">
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#3C3C4C] p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wide">Total Assets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">234</p>
            </div>
            <Package className="w-8 h-8 text-redhalo-navy dark:text-redhalo-mint" />
          </div>
        </div>
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#3C3C4C] p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wide">Assigned Assets</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">189</p>
            </div>
            <Laptop className="w-8 h-8 text-emerald-600 dark:text-emerald-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#3C3C4C] p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wide">In Maintenance</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">12</p>
            </div>
            <AlertCircle className="w-8 h-8 text-amber-600 dark:text-amber-400" />
          </div>
        </div>
        <div className="bg-white dark:bg-[#1D1D1D] border border-gray-200 dark:border-[#3C3C4C] p-4 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-sm font-semibold uppercase tracking-wide">Total Value</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">$324,892</p>
            </div>
            <Download className="w-8 h-8 text-redhalo-orange" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Assets;
